import React from 'react';
import { Navigate } from 'react-router-dom';
import { AssessmentProvider, useAssessmentContext, useAssessmentTooltips } from './AssessmentContext';
import TestDashboard from './dashboard/TestDashboard';
import TemplateBuilder from './Templates/TemplateBuilder';
import QuestionBankManager from './QuestionBank/QuestionBankManager';
import HRUploadManager from './HRUpload/HRUploadManager';
import MemberAssessmentDashboard from './student/MemberAssessmentDashboard';
import Layout from '../../components/Layout/Layout';
import { useUser } from '../../contexts/UserContext';
import ResultsEvaluation from './evaluation/ResultsEvaluation';
import AnalyticsDashboard from './dashboard/AnalyticsDashboard';
import {
  isAssessmentAdmin,
  isAssessmentBlocked,
  isAssessmentStudent,
} from '../../utils/assessmentStudent';

const FULL_HEIGHT_MODULES = new Set(['templates', 'questionBank', 'hrUpload']);

const AssessmentContent = () => {
  useAssessmentTooltips();
  const { activeModule, moduleReturnTo, returnFromModule, selectedTestId } = useAssessmentContext();
  const useFullHeightLayout = FULL_HEIGHT_MODULES.has(activeModule);

  return (
    <div
      className={`bg-[#fcfcfc] flex flex-col max-lg:h-auto max-lg:overflow-visible ${
        useFullHeightLayout
          ? 'h-full min-h-0 overflow-hidden'
          : 'min-h-0 overflow-visible'
      }`}
    >
      <div
        className={`w-full max-lg:flex-initial max-lg:h-auto max-lg:overflow-visible ${
          useFullHeightLayout ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-0 overflow-visible'
        }`}
      >
        {activeModule === 'inventory' && <TestDashboard />}
        {activeModule === 'questionBank' && (
          <QuestionBankManager onBack={returnFromModule} returnToTemplates={moduleReturnTo === 'templates'} />
        )}
        {activeModule === 'templates' && (
          <TemplateBuilder onBack={returnFromModule} />
        )}
        {activeModule === 'evaluation' && (
          <ResultsEvaluation
            assessmentId={selectedTestId}
            onBack={returnFromModule}
          />
        )}
        {activeModule === 'hrUpload' && (
          <HRUploadManager onBack={returnFromModule} />
        )}
        {activeModule === 'analytics' && (
          <AnalyticsDashboard onBack={returnFromModule} />
        )}
      </div>
    </div>
  );
};

const AssessmentPage = () => {
  const { user, loading } = useUser();

  React.useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const handleNavigationIntercept = (url) => {
      if (url) {
        const urlStr = String(url);
        if ((urlStr.includes('/home?tab=employees') || urlStr.includes('/home')) && 
            sessionStorage.getItem('assessment_nav_from_result_analysis') === 'true') {
          sessionStorage.removeItem('assessment_nav_from_result_analysis');
          window.location.href = '/assessment';
          return true;
        } else if (!urlStr.includes('/employee-details')) {
          sessionStorage.removeItem('assessment_nav_from_result_analysis');
        }
      }
      return false;
    };

    window.history.pushState = function(state, unused, url) {
      if (handleNavigationIntercept(url)) return;
      return originalPushState.apply(this, arguments);
    };

    window.history.replaceState = function(state, unused, url) {
      if (handleNavigationIntercept(url)) return;
      return originalReplaceState.apply(this, arguments);
    };

    return () => {
      // Keep globally active so that it runs when user navigates away to /employee-details and clicks Back.
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (isAssessmentBlocked(user)) {
    return <Navigate to="/tickets" replace />;
  }

  const showAdminInventory = isAssessmentAdmin(user);

  return (
    <AssessmentProvider>
      <Layout noSidebar={true}>
        {showAdminInventory ? <AssessmentContent /> : <MemberAssessmentDashboard/>}
      </Layout>
    </AssessmentProvider>
  );
};

export default AssessmentPage;
